import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { auth } from '../firebase-config';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Set persistence to LOCAL to maintain auth state across tabs and refreshes
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      console.error('Error setting auth persistence:', error);
    });

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        // Store user metadata only — token is fetched lazily by apiClient
        const userData = {
          userId: firebaseUser.uid,
          userName: firebaseUser.displayName || '',
          userEmail: firebaseUser.email || '',
          userCreatedAt: firebaseUser.metadata.creationTime,
          userLastLogin: firebaseUser.metadata.lastSignInTime,
        };

        Object.keys(userData).forEach(key => {
          localStorage.setItem(key, userData[key]);
        });

        setUser(firebaseUser);
      } else {
        setUser(null);
        // Only remove app-specific keys — never call localStorage.clear() because
        // that would also delete Firebase's own auth keys (firebase:authUser:…),
        // triggering onAuthStateChanged(null) in every other open tab via the
        // storage event and causing a cross-tab logout cascade.
        ['userId', 'userName', 'userEmail', 'userCreatedAt', 'userLastLogin',
          'authToken', 'tokenTimestamp', 'companyId', 'companyName'].forEach(
          key => localStorage.removeItem(key)
        );
      }

      setIsLoading(false);
      setIsInitialized(true);
    });

    // Set up automatic token refresh with better error handling
    const setupTokenRefresh = () => {
      // Always schedule the interval; the callback checks currentUser at execution time
      const refreshInterval = setInterval(async () => {
        try {
          const currentUser = auth.currentUser;
          if (currentUser) {
            const freshToken = await currentUser.getIdToken(true);
            const tokenTimestamp = Date.now();

            localStorage.setItem('authToken', freshToken);
            localStorage.setItem('tokenTimestamp', tokenTimestamp.toString());

            // Broadcast token refresh to other tabs
            if (window.BroadcastChannel) {
              const channel = new BroadcastChannel('auth-channel');
              channel.postMessage({
                type: 'TOKEN_REFRESHED',
                token: freshToken,
                timestamp: tokenTimestamp
              });
              channel.close();
            }
          }
        } catch (error) {
          console.error('Error refreshing token:', error);
          // Do NOT cancel the interval — a transient error should not stop future refreshes
        }
      }, 45 * 60 * 1000); // 45 minutes

      return () => clearInterval(refreshInterval);
    };

    // Listen for token refresh messages from other tabs
    const setupBroadcastListener = () => {
      if (window.BroadcastChannel) {
        const channel = new BroadcastChannel('auth-channel');

        channel.onmessage = (event) => {
          if (event.data.type === 'TOKEN_REFRESHED') {
            localStorage.setItem('authToken', event.data.token);
            localStorage.setItem('tokenTimestamp', event.data.timestamp.toString());
          }
        };

        return () => channel.close();
      }
    };

    // Set up token refresh after auth state is initialized
    const tokenRefreshCleanup = setupTokenRefresh();
    const broadcastCleanup = setupBroadcastListener();

    return () => {
      unsubscribe();
      if (tokenRefreshCleanup) {
        tokenRefreshCleanup();
      }
      if (broadcastCleanup) {
        broadcastCleanup();
      }
    };
  }, []);

  const value = {
    user,
    isLoading,
    isInitialized,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
